import { Point, Gradient } from '../../../../types'
import {
  GradientColorInterpolation,
  Gradient as SketchSceneGradient,
} from '../../../../../inspector'
import { roundWithLocale } from '@sketch/utils'

// Taken from Sketch
// https://github.com/sketch-hq/Sketch/blob/b373bfc2207956a93f23116cae2770fa296c3e9a/Modules/SketchModel/Source/MSLayer%2BCSS.m#L208-L211
const slopeBetweenPoints: (a: Point, b: Point) => number = (a, b) =>
  Math.atan2(b.y - a.y, b.x - a.x)

const radiansToDegrees = (radians: number) => radians * (180.0 / Math.PI)

const gradientAngle: (
  gradient: Gradient | SketchSceneGradient
) => number = gradient => {
  const slope = slopeBetweenPoints(gradient.from, gradient.to)
  return 90 + radiansToDegrees(slope)
}

const gradientStops: (
  gradient: Gradient | SketchSceneGradient
) => string = gradient => {
  const stops = [...gradient.stops]
    .sort((lhs, rhs) => lhs.position - rhs.position)
    .map(
      stop =>
        `rgba(${stop.color.red}, ${stop.color.green}, ${stop.color.blue}, ${
          stop.color.alpha
        }) ${roundWithLocale(stop.position * 100, 0)}%`
    )
    .join(', ')
  return stops
}

const colorInterpolationSuffix = (gradient: SketchSceneGradient): string => {
  switch (gradient.colorInterpolation) {
    case GradientColorInterpolation.Oklab:
      return 'in oklab'
    case GradientColorInterpolation.Oklch:
      return 'in oklch'
    case GradientColorInterpolation.Rgb:
      return ''
  }
}

const gradientBackground: (
  gradient: Gradient | SketchSceneGradient
) => string = gradient => {
  if (gradient.type === 'angular') {
    return 'transparent'
  }

  if (gradient.type === 'radial') {
    return `radial-gradient(${gradientStops(gradient)})`
  }

  const angle = gradientAngle(gradient)
  const stops = gradientStops(gradient)
  if (
    'colorInterpolation' in gradient &&
    gradient.colorInterpolation !== GradientColorInterpolation.Rgb
  ) {
    const suffix = colorInterpolationSuffix(gradient)
    return `linear-gradient(${angle}deg ${suffix}, ${stops})`
  }

  return `linear-gradient(${angle}deg, ${stops})`
}

export { gradientBackground, gradientAngle, gradientStops }
